import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { AddEditNoteMode } from '@core/enums/add-edit-note-mode.enum';
import { LeaveCaseNoteVisibilityOption } from '@core/enums/leave-case-note-visibility-option.enum';
import { StiiraError } from '@core/models';
import { CaseNote } from '@core/models/leave-admin/case-note.model';
import { EmployeeNote } from '@core/models/leave-admin/employees/employee-record.model';
import { EmployerNote } from '@core/models/leave-admin/employers/employer-note.model';
import { ConstantsService, ErrorService, LayoutService } from '@core/services';
import { EmployeeRecordStoreService } from '@core/services/employee-record-store.service';
import { EmployerProfileStoreService } from '@core/services/employer-profile-store.service';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { ManageEmployeesService } from '@core/services/manage-employees.service';
import { ManageEmployersService } from '@core/services/manage-employers.service';
import { FormArray, FormBuilder, UntypedFormGroup } from '@angular/forms';
import { isNumber } from '@microsoft/applicationinsights-core-js';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { Subject, Observable } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { takeUntil } from 'rxjs/operators';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';

@Component({
    selector: 'app-edit-case-note',
    templateUrl: './edit-case-note.component.html',
    styleUrls: ['./edit-case-note.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditCaseNoteComponent implements OnInit {
  static readonly componentName = 'EditCaseNoteComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  public form: UntypedFormGroup;
  public isSubmitting$: Subject<boolean>;
  public showVisibilityOptions: boolean = false;
  public visibilityOptionlabels: string[] = [];
  public visibilityOptionIcons: string[];

  public position = position;
  public onDragEnded = onDragEnded;

  private formInitValues: any;
  private visibilityOptionsEnum = LeaveCaseNoteVisibilityOption;
  private visibilityOptions: (string | LeaveCaseNoteVisibilityOption)[];
  private destroy$: Subject<void> = new Subject<void>();
  private formChangeEmitted: boolean = false;

  public isMobile$: Observable<boolean>;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  get visibilityOptionsFormArray(): FormArray {
    return this.form.controls.visibilityOptions as FormArray;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { 
      sysText: any;
      unsavedSysText: any;
      note: CaseNote | EmployerNote | EmployeeNote;
      mode: AddEditNoteMode;
    },
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EditCaseNoteComponent>,
    private layoutService: LayoutService,
    private leaveAdminService: LeaveAdminService,
    private leaveAdminStore: LeaveAdminStoreService,
    private manageEmployerService: ManageEmployersService,
    private employerProfileStore: EmployerProfileStoreService,
    private manageEmployeesService: ManageEmployeesService,
    private employeeRecordStore: EmployeeRecordStoreService,
    private errorService: ErrorService,
    private constants: ConstantsService,
    private dialog: MatDialog
    ) {
    this.form = this.fb.group({ 
      message: [null],
      visibilityOptions: this.fb.array([])
    });
    this.isSubmitting$ = new Subject();
    this.visibilityOptionIcons = this.constants.VISIBILITY_ICONS;
   }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close());

    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public close(): void {
    if (this.noChanges) {
      this.dialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }
  
  public save(): void {
    switch(this.data.mode) {
      case AddEditNoteMode.Employee: {
        const note: EmployeeNote = {
          ...this.data.note,
          message: this.form.controls.message.value
        }
        this.manageEmployeesService
          .editEmployeeNote(note)
          .pipe(indicate(this.isSubmitting$))
          .subscribe((res) => {
            this.employeeRecordStore.updateEmployeeNote(res);
            this.dialogRef.close();
            },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
        break;
      }
      case AddEditNoteMode.Employer: {
        const note: EmployerNote = {
          ...this.data.note,
          message: this.form.controls.message.value
        }
        this.manageEmployerService
          .postEmployerNoteEdit(note)
          .pipe(indicate(this.isSubmitting$))
          .subscribe((res) => {
            this.employerProfileStore.updateEmployerNote(res);
            this.dialogRef.close();
            },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
        break;
      }
      case AddEditNoteMode.LeaveCase: {
        const noteData = this.data.note as CaseNote;
        const note: CaseNote = {
          ...noteData,
          message: this.form.controls.message.value,
          visibilitySettings: []
        }
        this.visibilityOptions.forEach((id, index) => {
          if (this.visibilityOptionsFormArray.controls[index].value) {
            note.visibilitySettings.push(id as LeaveCaseNoteVisibilityOption);
          }
        })

        this.leaveAdminService
          .editCaseNote(note)
          .pipe(indicate(this.isSubmitting$))
          .subscribe((res) => {
            this.leaveAdminStore.updateCaseNote(res);
            this.dialogRef.close();
            },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
        break;
      }
    }
  }

  private buildVisibilityOptions(note: CaseNote): void {
    this.visibilityOptions.forEach(id => {
      this.visibilityOptionlabels.push(this.data.sysText['visibilityOption_' + id]);
      this.visibilityOptionsFormArray
        .push(this.fb.control(note.visibilitySettings?.includes(id as LeaveCaseNoteVisibilityOption)));
    });
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedSysText,
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private setupForm(): void {
    if (this.data.mode == AddEditNoteMode.LeaveCase) {
      const note = this.data.note as CaseNote;
      if (note.canEditVisibility) {
        this.visibilityOptions = Object.values(this.visibilityOptionsEnum).filter(v => isNumber(v));
        this.buildVisibilityOptions(note);
        this.showVisibilityOptions = note.canEditVisibility;
      }
    }

    if (!this.data.note.canEditNote) {
      this.form.controls.message.disable();
    }

    this.form.patchValue({ message: this.data.note.message });
    this.formInitValues = { ...this.form.value };
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.formChangeEmitted && !this.noChanges) {
          this.isEditing.emit(true);
          this.formChangeEmitted = true;
        } else if (this.noChanges) {
          this.isEditing.emit(false);
          this.formChangeEmitted = false;
        }
      });
  }
}